import React, { useState, useRef } from 'react';

import blurTop from '../../assets/splashScreen/Blur.svg'
import { useNavigate } from 'react-router-dom';


/**
 * ConfirmCodeScreen Component
 * Renders the 4-digit confirmation code input screen based on the provided PDF design.
 * It includes responsiveness for PWA use.
 */
export default function ConfirmCodeScreen() {
    const navigate = useNavigate()
  const email = "lucasscott3@email.com"; // Mock email for display
  const [code, setCode] = useState(['', '', '', '']);
  // Refs for focusing next/previous input fields
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const handleCodeChange = (e, index) => {
    const value = e.target.value;
    const newCode = [...code];

    // Only take the first digit and update state
    newCode[index] = value.slice(-1);
    setCode(newCode);

    // Auto-focus to the next input if a digit was entered and it's not the last one
    if (value && index < 3 && inputRefs[index + 1].current) {
      inputRefs[index + 1].current.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle backspace to clear current and move to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0 && inputRefs[index - 1].current) {
      // Clear the current box before moving back if it contains a value
      if (code[index] !== '') {
          const newCode = [...code];
          newCode[index] = '';
          setCode(newCode);
      } else {
        inputRefs[index - 1].current.focus();
      }
    }
  };

  const isCodeComplete = code.every(digit => digit.length === 1);

  const handleResendCode = () => {
    // Placeholder for API call to resend the code
    console.log("Resend code clicked. Implement API logic here.");
    // NOTE: In a real PWA, you might show a cooldown timer or success message here.
  };
  
  const handleContinue = () => {
    if (isCodeComplete) {
      // In a real app, this would be an API call to verify the code
      console.log(`Code entered: ${code.join('')}. Continuing to the next step.`);
      // IMPORTANT: Swapping alert() with a console log or custom modal
      console.log("Code verified! Navigation to main app.");

      navigate('/home')
    }
  };

  return (
    // Outer container for responsiveness and full screen height
    <div className="min-h-screen bg-white text-gray-800 px-6 py-10 flex flex-col relative overflow-hidden">
      
      {/* Decorative Image at the Top */}
      <img
        src={blurTop}
        alt="Decorative background blur"
        className="absolute top-0 left-0 w-full h-auto pointer-events-none select-none opacity-50 max-h-48 object-cover"
      />

      {/* Content area, locked to a standard mobile width and centered */}
      <div className="relative z-10 w-full max-w-sm mx-auto text-center">
        
        {/* Title */}
        <h1 className="text-[20px] font-[500] text-[#307CF8]">Enter confirmation code</h1>
        
        {/* Code Description from PDF */}
        <p class="text-gray-[400] text-[12px] mt-2 mb-12">
          A 4-digit code was sent to <br />
          <span className="font-semibold text-gray-700">{email}</span>
        </p>
      </div>

      {/* Code Input Fields (Responsive, centered) */}
      <div className="flex justify-between space-x-4 w-full max-w-[300px] mx-auto z-10">
        {[...Array(4)].map((_, index) => (
          <input
            key={index}
            ref={inputRefs[index]}
            type="text" 
            inputMode="numeric"
            pattern="\d*" // Suggests numeric input for mobile keyboard
            maxLength="1"
            value={code[index]}
            onChange={(e) => handleCodeChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className="w-12 h-12 sm:w-12 sm:h-12 text-center text-xl font-extrabold rounded-xl border-2 border-gray-300 focus:border-blue-600 outline-none transition-colors shadow-md"
          />
        ))}
      </div>

      {/* Actions (Resend Code and Continue) from PDF */}
      <div className="mt-16 flex flex-col items-center w-full max-w-sm mx-auto z-10">
        
        {/* Resend Code Link */}
        <button 
          onClick={handleResendCode}
          className="text-blue-600 font-medium text-sm mb-6 hover:text-blue-800 transition-colors"
        >
          Resend code
        </button>

        {/* Continue Button */}
        <button 
          onClick={handleContinue}
          disabled={!isCodeComplete} // Disable if code is not fully entered
          className={`w-full py-3 rounded-xl text-lg font-semibold text-white transition-colors shadow-lg ${
            isCodeComplete 
              ? 'bg-blue-600 hover:bg-blue-700' 
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
}